// Unsaved report edits stay in this tab across navigation, refresh, and login.
// Session storage prevents two tabs from overwriting each other's drafts and
// avoids leaving report contents in long-lived local storage on shared devices.
// Callers may offer recovery only after a fresh, authorized editable report GET.

import { storageGet, storageRemove, storageSet } from "./storage";

/** @typedef {{schema: 1, id: string, baseVersion: number, content: Record<string, any>, branding: Record<string, any>, savedAt: number}} ReportDraft */

const MAX_DRAFT_CHARACTERS = 1000000;

/** @param {unknown} value @returns {value is Record<string, any>} */
const isObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

/** @param {string} uuid */
const draftKey = (uuid) => `liq_report_draft:${uuid}`;

/** @param {string} uuid @returns {ReportDraft | null} */
export const loadReportDraft = (uuid) => {
  if (!uuid) return null;
  try {
    const serialized = storageGet(draftKey(uuid));
    if (!serialized || serialized.length > MAX_DRAFT_CHARACTERS) return null;
    const draft = JSON.parse(serialized);
    if (
      !isObject(draft) ||
      draft.schema !== 1 ||
      typeof draft.id !== "string" ||
      !draft.id ||
      !Number.isSafeInteger(draft.baseVersion) ||
      draft.baseVersion < 1 ||
      !isObject(draft.content) ||
      !Array.isArray(draft.content.sections) ||
      !isObject(draft.branding) ||
      !Number.isFinite(draft.savedAt)
    ) {
      return null;
    }
    return /** @type {ReportDraft} */ (draft);
  } catch {
    return null;
  }
};

/**
 * Return the saved record only after verifying storage accepted it. A quota or
 * privacy failure must not let navigation copy promise that a backup exists.
 * @param {string} uuid
 * @param {{baseVersion: number, content: Record<string, any>, branding: Record<string, any>}} value
 * @returns {ReportDraft | null}
 */
export const saveReportDraft = (uuid, value) => {
  if (!uuid) return null;
  try {
    if (
      !Number.isSafeInteger(value.baseVersion) ||
      value.baseVersion < 1 ||
      !isObject(value.content) ||
      !Array.isArray(value.content.sections) ||
      !isObject(value.branding)
    ) {
      return null;
    }
    /** @type {ReportDraft} */
    const draft = {
      schema: 1,
      id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`,
      baseVersion: value.baseVersion,
      content: value.content,
      branding: value.branding,
      savedAt: Date.now(),
    };
    const serialized = JSON.stringify(draft);
    if (serialized.length > MAX_DRAFT_CHARACTERS) return null;
    storageSet(draftKey(uuid), serialized);
    return storageGet(draftKey(uuid)) === serialized ? draft : null;
  } catch {
    return null;
  }
};

/**
 * An old acknowledgment may clear only its own draft, never a newer edit.
 * @param {string} uuid
 * @param {string | null | undefined} expectedId
 * @returns {boolean}
 */
export const clearReportDraft = (uuid, expectedId) => {
  if (!expectedId || loadReportDraft(uuid)?.id !== expectedId) return false;
  storageRemove(draftKey(uuid));
  return storageGet(draftKey(uuid)) === null;
};
